import React, { useState } from "react";
import { 
  TextField, 
  Grid, 
  Button, 
  Box,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Typography
} from "@mui/material";
import {
  Person,
  Assignment,
  Phone,
  Email,
  Save,
  LocationOn,
  HomeWork,
  Apartment,
  LocationCity,
  Public,
  Room,
  Search
} from '@mui/icons-material';
import { TextMaskPhone, TextMaskZipCode } from "./TextMaskCustom";
import axios from "axios";

const textFieldStyles = {
  "& label.Mui-focused": {
    color: "#536777",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset, &:hover fieldset": {
      opacity: 0.24,
    },
  },
};

const userFields = [
  { 
    id: "name", 
    label: "Nome", 
    icon: <Person sx={{ color: 'action.active' }} />,
    tooltip: "Seu nome",
    disabled: false
  },
  { 
    id: "last_name", 
    label: "Sobrenome",
    icon: <Person sx={{ color: 'action.active' }} />,
    tooltip: "Seu sobrenome",
    disabled: false
  },
  { 
    id: "document", 
    label: "Documento",
    icon: <Assignment sx={{ color: 'action.active' }} />,
    tooltip: "Seu documento de identificação",
    disabled: true,
    value: "document"
  },
  { 
    id: "email", 
    label: "E-mail",
    icon: <Email sx={{ color: 'action.active' }} />,
    tooltip: "Seu e-mail",
    disabled: true
  },
  { 
    id: "phone", 
    label: "Telefone",
    icon: <Phone sx={{ color: 'action.active' }} />,
    tooltip: "Seu telefone",
    disabled: false,
    mask: TextMaskPhone
  }
];

const addressFields = [
  { 
    id: "street", 
    label: "Endereço Atual", 
    icon: <HomeWork sx={{ color: 'action.active' }} />,
    tooltip: "Endereço completo",
    sm: 12
  },
  { 
    id: "number", 
    label: "Número", 
    icon: <Room sx={{ color: 'action.active' }} />,
    tooltip: "Número do endereço",
    sm: 4,
    type: "tel"
  },
  { 
    id: "complement", 
    label: "Complemento",
    icon: <Apartment sx={{ color: 'action.active' }} />,
    tooltip: "Apartamento, sala, etc",
    sm: 8
  },
  { 
    id: "neighborhood", 
    label: "Bairro",
    icon: <LocationOn sx={{ color: 'action.active' }} />,
    tooltip: "Nome do bairro",
    sm: 6
  },
  { 
    id: "city", 
    label: "Cidade",
    icon: <LocationCity sx={{ color: 'action.active' }} />,
    tooltip: "Nome da cidade",
    sm: 6
  },
  { 
    id: "state", 
    label: "Estado",
    icon: <Public sx={{ color: 'action.active' }} />,
    tooltip: "Estado (UF)",
    sm: 12
  }
];

export default function ProfileForm({ currentUser = [], updateUser }) {
  const [loading, setLoading] = useState(false);
  const [userErrors, setUserErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [user, setUser] = useState({
    id: currentUser.id,
    name: currentUser.name,
    last_name: currentUser.last_name,
    email: currentUser.email,
    phone: currentUser.phone,
  });

  const [address, setAddress] = useState({
    zipCode: currentUser.last_billing_address?.zip_code,
    street: currentUser.last_billing_address?.address,
    number: currentUser.last_billing_address?.number,
    complement: currentUser.last_billing_address?.complement,
    neighborhood: currentUser.last_billing_address?.neighborhood,
    city: currentUser.last_billing_address?.city,
    state: currentUser.last_billing_address?.state,
  });

  const [mustSearchAddress, setMustSearchAddress] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (field) => (e) => {
    setAddress(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleCepChange = (e) => {
    setMustSearchAddress(true);
    setAddress(prev => ({ ...prev, zipCode: e.target.value }));
  };

  const getAddress = async () => {
    if (!mustSearchAddress || !address.zipCode?.length === 8) return;
    
    setIsSearching(true);
    try {
      const { data } = await axios.get(
        `https://viacep.com.br/ws/${address.zipCode}/json/`
      );
      
      if (data.erro) return;

      const {
        logradouro: street,
        localidade: city,
        bairro: neighborhood,
        uf: state,
      } = data;

      setAddress(prev => ({
        ...prev,
        street,
        city,
        neighborhood,
        state,
        number: "",
        complement: "",
      }));
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setIsSearching(false);
      setMustSearchAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await updateUser(user, address);
      showMessage(res.data.message, 'success');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      showMessage(err.response?.data.message, 'error');
      setUserErrors(err.response?.data.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showMessage = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const renderTextField = ({ id, label, icon, tooltip, disabled = false, mask }) => (
    <Box 
      sx={{ 
        display: "flex",
        alignItems: "flex-start",
        mb: 2,
        gap: 2
      }}
    >
      <Tooltip title={tooltip} placement="left" arrow>
        <Box 
          sx={{ 
            mt: 1.5,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {icon}
        </Box>
      </Tooltip>

      <TextField
        fullWidth
        id={id}
        name={id}
        label={label}
        variant="outlined"
        value={id === "document" ? currentUser.document : user[id]}
        disabled={disabled}
        error={Boolean(userErrors[id])}
        helperText={userErrors[id]?.join(", ") || ""}
        onChange={handleChange}
        sx={textFieldStyles}
        {...(mask && {
          InputProps: { inputComponent: mask },
          InputLabelProps: { shrink: true }
        })}
      />
    </Box>
  );

  const renderAddressField = ({ id, label, icon, tooltip, sm = 12, type = "text" }) => (
    <Grid item xs={12} sm={sm} key={id}>
      <Box 
        sx={{ 
          display: "flex",
          alignItems: "flex-start",
          gap: 2
        }}
      >
        <Tooltip title={tooltip} placement="left" arrow>
          <Box 
            sx={{ 
              mt: 1.5,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {icon}
          </Box>
        </Tooltip>

        <TextField
          fullWidth
          id={`address-${id}`}
          name={id}
          label={label}
          variant="outlined"
          type={type}
          value={address[id] || ""}
          error={Boolean(userErrors[id])}
          helperText={userErrors[id]}
          onChange={handleAddressChange(id)}
          sx={textFieldStyles}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container>
        <form onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
          <Grid 
            item 
            xs={12} 
            sx={{ 
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="h6" 
                color="textSecondary"
                sx={{
                  fontWeight: 500
                }}
              >
                Dados Pessoais
              </Typography>
            </Box>

            {userFields.map(renderTextField)}

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography 
                variant="h6" 
                color="textSecondary"
                sx={{
                  fontWeight: 500
                }}
              >
                Endereço de Cobrança
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2
                  }}
                >
                  <Tooltip title="Digite o CEP para buscar o endereço" placement="left" arrow>
                    <Box 
                      sx={{ 
                        mt: 1.5,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Search sx={{ color: 'action.active' }} />
                    </Box>
                  </Tooltip>

                  <TextField
                    fullWidth
                    id="address-cep"
                    label="CEP"
                    variant="outlined"
                    value={address.zipCode || ""}
                    onChange={handleCepChange}
                    onBlur={getAddress}
                    error={Boolean(userErrors.zip_code)}
                    helperText={userErrors.zipCode}
                    InputProps={{
                      inputComponent: TextMaskZipCode,
                      endAdornment: isSearching && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldStyles}
                  />
                </Box>
              </Grid>

              {addressFields.map(renderAddressField)}
            </Grid>

            <Box 
              sx={{ 
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: 3
              }}
            >
              <Button
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                disableElevation
                sx={{
                  color: "#fff",
                  minWidth: "120px",
                  borderRadius: "10px",
                  fontWeight: 900,
                  bgcolor: "#239CC4",
                  "&:hover": {
                    bgcolor: "#1b7a99"
                  },
                  "&:disabled": {
                    bgcolor: "action.disabledBackground"
                  }
                }}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Grid>
        </form>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
